import React from "react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { safeError } from "@/lib/safeError";
import { trackEvent } from "@/lib/analytics";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    console.log('[AuthCallback] Component mounted, starting auth flow...');

    // Get redirect URL from query params
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const code = params.get('code');
    const errorParam = params.get('error');
    const state = params.get('state');
    const nextUrl = params.get('next') || '/ideas';
    
    console.log('[AuthCallback] URL params:', { hasCode: !!code, error: errorParam, state, nextUrl });

    // If the provider returned an error in the URL, surface it immediately
    if (errorParam) {
      console.error('[AuthCallback] Provider error received in callback:', errorParam);
      toast({
        title: 'Authentication failed',
        description: errorParam,
        variant: 'destructive',
      });
      navigate('/auth');
      return;
    }

    // Subscribe to auth state changes - only act on SIGNED_IN to avoid premature redirects
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[AuthCallback] Auth state change:', { event, hasSession: !!session, userId: session?.user?.id?.slice(0,8) + '...' });

      if (event === 'SIGNED_IN' && session) {
        console.log('[AuthCallback] Successfully signed in, redirecting to:', nextUrl);
        trackEvent('signup_completed');
        navigate(nextUrl);
      } else if (event === 'SIGNED_OUT') {
        console.warn('[AuthCallback] Received SIGNED_OUT during callback flow');
      }
    });

    // Also check for existing session immediately
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        const e = safeError(error);
        console.error('[AuthCallback] Session retrieval error:', e.name, e.message);
        toast({
          title: 'Error',
          description: e.message || 'Authentication error occurred',
          variant: 'destructive',
        });
        navigate('/auth');
      } else if (session) {
        console.log('[AuthCallback] Found existing session, redirecting to:', nextUrl);
        navigate(nextUrl);
      } else {
        console.log('[AuthCallback] No session found yet, waiting for auth state change...');
      }
    });

    // Fallback: after 10s, if still no session, send back to sign-in
    const timeoutId = window.setTimeout(async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      console.log('[AuthCallback] Fallback session check after timeout:', { hasSession: !!session, error });
      if (session) {
        navigate(nextUrl);
      } else {
        toast({
          title: 'Authentication failed',
          description: 'No active session was found. Please try again.',
          variant: 'destructive',
        });
        navigate('/auth');
      }
    }, 10000);

    return () => {
      console.log('[AuthCallback] Cleaning up auth listener');
      subscription.unsubscribe();
      window.clearTimeout(timeoutId);
    };
  }, [navigate, toast]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
}
